/**
 * Bid orchestration (DESIGN §4.5): scenarios A (today) / B (switch) /
 * C (Octopus bid), the incremental savings waterfall, per-term totals for
 * 12/18/24 months, monthly arrays for charting, the rep-only VPP program
 * value, and an echo of every assumption used.
 *
 * `BidInput` and `BidResult` are the contract with the UI (TODO-005).
 *
 * Savings only: there is deliberately no system price, financing, lifetime
 * savings, ROI, payback or LCOE anywhere in the result (ES TODO-010 parity).
 * The VPP credit is retained by Octopus and is never subtracted from a
 * customer bill; it appears only under `repOnly.vppProgramValue`.
 *
 * Pure: no DOM, network or global state. Config is injected (defaults to the
 * shipped `src/config` data).
 */
import {
  ENGINE_DEFAULTS,
  OCTOPUS_PLAN,
  REGIONS,
  TDUS,
  TERM_OPTIONS,
  isValidRegionForTdu,
  type BuybackApplyMode,
  type EngineDefaults,
  type LoadArchetype,
  type OctopusPlanConfig,
  type RateAlternate,
  type RateEntry,
  type RegionConfig,
  type RegionId,
  type TduConfig,
  type TduId,
  type TermMonths,
} from "../config";
import { simulateBattery, type BatteryConfig, type BatteryResult } from "./battery";
import {
  NO_BUYBACK,
  aggregateFlows,
  billTerm,
  type BuybackRules,
  type MonthlyFlows,
  type RetailPricing,
  type TduPricing,
  type TermBills,
} from "./billing";
import { HOURS_IN_YEAR, sumByMonth } from "./calendar";
import {
  applyNestCoolingReduction,
  buildEvProfile,
  carveOutMonthlyKwh,
  composeLoad,
  generateHomeLoad,
  type UsageInput,
} from "./load";
import { generateSolarProfile } from "./solar";

// ═══════════════════════════════════════════════════════════════════════════
// INPUT CONTRACT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The customer's current REP plan (scenario A).
 * - `itemized`: energy ¢/kWh + base fee from the bill; TDU is added on top.
 * - `allIn`: a single all-in $/kWh (e.g. the EFL "average price") that already
 *   includes TDU charges; no TDU is added. Optional base fee if not in the rate.
 */
export type CurrentPlanInput =
  | {
      readonly mode: "itemized";
      /** REP energy charge, $/kWh (e.g. 0.145). */
      readonly energyRate: number;
      /** REP base / monthly fee, $/month (e.g. 9.95). */
      readonly baseFee: number;
    }
  | {
      readonly mode: "allIn";
      /** All-in price including TDU, $/kWh (e.g. 0.165). */
      readonly rate: number;
      /** Any fee not included in `rate`, $/month. Default 0. */
      readonly baseFee?: number;
    };

/** ☀️ Solar toggle (scenario C). */
export interface SolarInput {
  readonly enabled: boolean;
  /** System size, kWdc. Required unless `annualKwhOverride` is given. */
  readonly systemKwDc?: number;
  /** Installer's annual production estimate, kWh/yr. Wins over kW × yield. */
  readonly annualKwhOverride?: number;
  /** Default 25°. */
  readonly tiltDeg?: number;
  /** Default 180° (south). */
  readonly azimuthDeg?: number;
}

/** 🔋 Free battery toggle (scenario C). Self-consumption, home load only. */
export interface BatteryInput {
  readonly enabled: boolean;
  /** Capacity, kWh. Default 13.5. Also drives the VPP program value. */
  readonly capacityKwh?: number;
  /** Power, kW. Default 5. */
  readonly powerKw?: number;
}

/** 🚗 EV toggle. */
export interface EvInput {
  readonly enabled: boolean;
  /**
   * - `new`: adds `annualMiles × kWhPerMile / 365` kWh/day overnight in ALL scenarios.
   * - `existing`: the EV is already in the usage history. Total usage is
   *   unchanged; the EV kWh are carved out of the history and modelled as the
   *   overnight `ev(h)` vector so the EV rate can apply to them.
   */
  readonly mode: "new" | "existing";
  /** Default 12,000 mi/yr. */
  readonly annualMiles?: number;
  /** Octopus EV charging rate, $/kWh (scenario C only). Default = Octopus energy rate (no discount). */
  readonly evRate?: number;
}

/** 🌡️ Nest toggle (scenario C only). */
export interface NestInput {
  readonly enabled: boolean;
  /** Fraction of cooling kWh saved, 0–1. Default 0.10. */
  readonly coolingReductionPct?: number;
}

/**
 * Rep overrides from the ⚙️ assumptions drawer. Any value given here replaces
 * the config default and is flagged `overridden: true` in the assumptions echo.
 */
export interface RateOverrides {
  /** Octopus energy rate per term, $/kWh. */
  readonly octopusEnergyRate?: Partial<Record<TermMonths, number>>;
  /** Octopus base fee, $/month. */
  readonly octopusBaseFee?: number;
  /** Buyback rate, $/kWh. */
  readonly buybackRate?: number;
  readonly buybackApplyMode?: BuybackApplyMode;
  readonly buybackRollover?: boolean;
  /** TDU customer charge, $/month. */
  readonly tduCustomerCharge?: number;
  /** TDU metering charge, $/month. */
  readonly tduMeteringCharge?: number;
  /** TDU volumetric rate, $/kWh (e.g. an entry from `volumetricRate.alternates`). */
  readonly tduVolumetricRate?: number;
  /** VPP credit, $/kWh·month (rep-only program value). */
  readonly vppUsdPerKwhMonth?: number;
  /** Battery reserve (min SOC) fraction, 0–1. */
  readonly vppReservePct?: number;
  /** Specific yield, kWh/kWdc·yr. */
  readonly regionYieldKwhPerKw?: number;
  readonly batteryRoundTripEfficiency?: number;
  readonly batteryMaxDailyCycles?: number;
  readonly evKwhPerMile?: number;
  /** EV smart-charge window [startHour, endHour). */
  readonly evChargeWindow?: readonly [number, number];
}

/** Everything the rep enters. */
export interface BidInput {
  readonly tdu: TduId;
  /** Must be served by `tdu` (see `regionsForTdu`). */
  readonly region: RegionId;
  /** Default `electric_heat` (most TX homes). Matters mainly for annual / average usage input. */
  readonly archetype?: LoadArchetype;
  readonly usage: UsageInput;
  readonly currentPlan: CurrentPlanInput;
  /**
   * Calendar month the contract starts, 0 = January. The UI should default
   * to next month (see {@link defaultStartMonth}).
   */
  readonly startMonth: number;
  /** Term highlighted in the UI. Default 12. All terms are always computed. */
  readonly selectedTerm?: TermMonths;
  readonly solar?: SolarInput;
  readonly battery?: BatteryInput;
  readonly ev?: EvInput;
  readonly nest?: NestInput;
  readonly overrides?: RateOverrides;
}

// ═══════════════════════════════════════════════════════════════════════════
// OUTPUT CONTRACT
// ═══════════════════════════════════════════════════════════════════════════

export type ScenarioId = "A" | "B" | "C";

/** One scenario's energy flows and bills for every term. */
export interface ScenarioResult {
  readonly id: ScenarioId;
  /** "Today", "Switch to Octopus", "Octopus Bid". */
  readonly label: string;
  /** Calendar-order (Jan–Dec) monthly grid flows. */
  readonly monthlyFlows: MonthlyFlows[];
  readonly annualImportsKwh: number;
  readonly annualExportsKwh: number;
  /**
   * Bills per term. `byTerm[T].months` is the chart series (length T, in
   * contract order starting at `startMonth`) and `byTerm[T].total` is the
   * term total. Money in unrounded USD.
   */
  readonly byTerm: Readonly<Record<TermMonths, TermBills>>;
}

/** Savings over a term (A − C or one waterfall step). Money in unrounded USD. */
export interface TermSavings {
  readonly termMonths: TermMonths;
  /** Savings over the whole term. */
  readonly total: number;
  /** `total / termMonths`. */
  readonly avgMonthly: number;
}

export type WaterfallStepId = "switch" | "solar" | "battery" | "ev" | "nest";

/**
 * One incremental savings step, computed in the fixed order
 * switch → solar → battery → EV rate → Nest. Steps for upgrades that are off
 * are omitted. Σ steps = A − C for every term.
 */
export interface WaterfallStep {
  readonly id: WaterfallStepId;
  readonly label: string;
  readonly emoji: string;
  readonly byTerm: Readonly<Record<TermMonths, TermSavings>>;
}

/** A value used in the bid, with provenance, for the drawer and the printout. */
export interface AssumptionEcho {
  /** Stable key, e.g. "tdu.volumetricRate". */
  readonly key: string;
  readonly label: string;
  readonly value: number | string | boolean | readonly number[];
  readonly unit: string;
  /** ISO date, or null for customer-provided values. */
  readonly effectiveDate: string | null;
  readonly source: string;
  /** Unconfirmed assumption: show ⚠. */
  readonly placeholder: boolean;
  /** Rep changed it in the drawer: label it on the printout. */
  readonly overridden: boolean;
  /** Config value when overridden. */
  readonly defaultValue?: number | string | boolean | readonly number[];
  readonly note?: string;
  /** Alternate published values the drawer can offer. */
  readonly alternates?: readonly RateAlternate<number>[];
  /** `customer` (rep-entered from the bill), `config`, or `engine` default. */
  readonly group: "customer" | "octopus" | "tdu" | "region" | "engine";
}

/** Energy totals for display. kWh. Monthly arrays are calendar order (Jan–Dec). */
export interface EnergySummary {
  /** Home load as calibrated to the usage input (excl. new-EV kWh; existing EV carved out). */
  readonly homeAnnualKwh: number;
  readonly homeMonthlyKwh: number[];
  /** Home load in scenario C after the Nest reduction (= home when Nest is off). */
  readonly homeAnnualKwhWithNest: number;
  readonly homeMonthlyKwhWithNest: number[];
  readonly evAnnualKwh: number;
  readonly evMonthlyKwh: number[];
  readonly solarAnnualKwh: number;
  readonly solarMonthlyKwh: number[];
  /** Present when the battery is on (scenario C dispatch). */
  readonly battery?: {
    readonly capacityKwh: number;
    readonly powerKw: number;
    readonly reserveKwh: number;
    readonly usableKwh: number;
    readonly annualChargedKwh: number;
    readonly annualDischargedKwh: number;
    readonly equivalentCycles: number;
  };
}

/** Values for the rep drawer only. Never shown to the customer or printed. */
export interface RepOnly {
  readonly vppProgramValue: {
    /** Battery capacity the credit is based on, kWh (0 when the battery is off). */
    readonly batteryKwh: number;
    readonly usdPerKwhMonth: number;
    /** `batteryKwh × usdPerKwhMonth`, $/month. */
    readonly monthly: number;
    /** `monthly × T`, $ per term. */
    readonly byTerm: Readonly<Record<TermMonths, number>>;
    /** Always true: Octopus keeps the credit to fund the free battery. */
    readonly retainedByOctopus: true;
  };
}

/** Everything the UI renders. */
export interface BidResult {
  readonly tdu: TduId;
  readonly region: RegionId;
  readonly startMonth: number;
  readonly selectedTerm: TermMonths;
  /** Terms computed, in display order: [12, 18, 24]. */
  readonly terms: readonly TermMonths[];
  readonly scenarios: Readonly<Record<ScenarioId, ScenarioResult>>;
  /** Headline savings A − C per term. */
  readonly savings: Readonly<Record<TermMonths, TermSavings>>;
  readonly waterfall: WaterfallStep[];
  readonly energy: EnergySummary;
  readonly repOnly: RepOnly;
  readonly assumptions: AssumptionEcho[];
  /** Non-fatal issues for the rep (e.g. battery without solar). */
  readonly warnings: string[];
}

/** Injected configuration (defaults to the shipped `src/config`). */
export interface BidConfig {
  readonly octopus: OctopusPlanConfig;
  readonly tdus: Readonly<Record<TduId, TduConfig>>;
  readonly regions: Readonly<Record<RegionId, RegionConfig>>;
  readonly defaults: EngineDefaults;
}

export const DEFAULT_BID_CONFIG: BidConfig = {
  octopus: OCTOPUS_PLAN,
  tdus: TDUS,
  regions: REGIONS,
  defaults: ENGINE_DEFAULTS,
};

/** Thrown for invalid input the UI should have prevented. */
export class BidInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BidInputError";
  }
}

/** Next calendar month (0–11) after `today`, the default contract start. */
export function defaultStartMonth(today: Date): number {
  return (today.getMonth() + 1) % 12;
}

// ═══════════════════════════════════════════════════════════════════════════
// IMPLEMENTATION
// ═══════════════════════════════════════════════════════════════════════════

const CUSTOMER_SOURCE = "Customer's current bill (rep-entered)";

function requireFiniteNonNeg(n: number | undefined, label: string): void {
  if (n !== undefined && (!Number.isFinite(n) || n < 0)) {
    throw new BidInputError(`${label} must be a non-negative number, got ${n}`);
  }
}

function perTerm<T>(fn: (t: TermMonths) => T): Record<TermMonths, T> {
  return { 12: fn(12), 18: fn(18), 24: fn(24) };
}

/** Builds a complete bid. See {@link BidInput} / {@link BidResult}. */
export function buildBid(input: BidInput, config: BidConfig = DEFAULT_BID_CONFIG): BidResult {
  const o = input.overrides ?? {};
  const warnings: string[] = [];
  const assumptions: AssumptionEcho[] = [];

  // ── Validate ────────────────────────────────────────────────────────────
  const tduCfg = config.tdus[input.tdu];
  if (!tduCfg) throw new BidInputError(`Unknown TDU "${input.tdu}"`);
  const regionCfg = config.regions[input.region];
  if (!regionCfg) throw new BidInputError(`Unknown region "${input.region}"`);
  if (!isValidRegionForTdu(input.tdu, input.region)) {
    throw new BidInputError(`Region "${input.region}" is not served by ${tduCfg.name}`);
  }
  if (!Number.isInteger(input.startMonth) || input.startMonth < 0 || input.startMonth > 11) {
    throw new BidInputError(`startMonth must be an integer 0–11, got ${input.startMonth}`);
  }
  const selectedTerm: TermMonths = input.selectedTerm ?? 12;
  if (!TERM_OPTIONS.includes(selectedTerm)) throw new BidInputError(`selectedTerm must be 12, 18 or 24`);
  const cp = input.currentPlan;
  if (cp.mode === "itemized") {
    requireFiniteNonNeg(cp.energyRate, "currentPlan.energyRate");
    requireFiniteNonNeg(cp.baseFee, "currentPlan.baseFee");
  } else {
    requireFiniteNonNeg(cp.rate, "currentPlan.rate");
    requireFiniteNonNeg(cp.baseFee, "currentPlan.baseFee");
  }

  // ── Resolve rates with overrides + echo ─────────────────────────────────
  function use<T extends number | string | boolean | readonly number[]>(
    key: string,
    label: string,
    group: AssumptionEcho["group"],
    entry: RateEntry<T>,
    override: T | undefined,
  ): T {
    const overridden = override !== undefined && JSON.stringify(override) !== JSON.stringify(entry.value);
    const value = override !== undefined ? override : entry.value;
    assumptions.push({
      key,
      label,
      value,
      unit: entry.unit,
      effectiveDate: entry.effectiveDate,
      source: overridden ? "Rep override (assumptions drawer)" : entry.source,
      placeholder: !overridden && entry.placeholder === true,
      overridden,
      ...(overridden ? { defaultValue: entry.value } : {}),
      ...(entry.note ? { note: entry.note } : {}),
      ...(entry.alternates ? { alternates: entry.alternates as unknown as readonly RateAlternate<number>[] } : {}),
      group,
    });
    return value;
  }

  // Customer plan echo
  if (cp.mode === "itemized") {
    assumptions.push(
      { key: "current.energyRate", label: "Current energy rate", value: cp.energyRate, unit: "$/kWh", effectiveDate: null, source: CUSTOMER_SOURCE, placeholder: false, overridden: false, group: "customer" },
      { key: "current.baseFee", label: "Current base fee", value: cp.baseFee, unit: "$/month", effectiveDate: null, source: CUSTOMER_SOURCE, placeholder: false, overridden: false, group: "customer" },
    );
  } else {
    assumptions.push(
      { key: "current.allInRate", label: "Current all-in rate (incl. TDU)", value: cp.rate, unit: "$/kWh", effectiveDate: null, source: CUSTOMER_SOURCE, placeholder: false, overridden: false, group: "customer" },
      { key: "current.baseFee", label: "Current fees not in rate", value: cp.baseFee ?? 0, unit: "$/month", effectiveDate: null, source: CUSTOMER_SOURCE, placeholder: false, overridden: false, group: "customer" },
    );
  }

  const oct = config.octopus;
  const octRate = perTerm((t) =>
    use(`octopus.energyRate.${t}`, `Octopus ${t}-month energy rate`, "octopus", oct.energyRateByTerm[t], o.octopusEnergyRate?.[t]),
  );
  const octBaseFee = use("octopus.baseFee", "Octopus base fee", "octopus", oct.baseFee, o.octopusBaseFee);
  const buyback: BuybackRules = {
    rate: use("octopus.buyback.rate", "Solar buyback rate", "octopus", oct.buyback.rate, o.buybackRate),
    applyMode: use("octopus.buyback.applyMode", "Buyback credits offset", "octopus", oct.buyback.applyMode, o.buybackApplyMode),
    rollover: use("octopus.buyback.rollover", "Buyback credits roll over", "octopus", oct.buyback.rollover, o.buybackRollover),
  };
  const vppRate = use("octopus.vpp.usdPerKwhMonth", "VPP credit (retained by Octopus)", "octopus", oct.vpp.usdPerKwhMonth, o.vppUsdPerKwhMonth);
  const reservePct = use("octopus.vpp.reservePct", "Battery VPP reserve", "octopus", oct.vpp.reservePct, o.vppReservePct);

  const tduCustomer = use("tdu.customerCharge", `${tduCfg.name} customer charge`, "tdu", tduCfg.customerCharge, o.tduCustomerCharge);
  const tduMeter = use("tdu.meteringCharge", `${tduCfg.name} metering charge`, "tdu", tduCfg.meteringCharge, o.tduMeteringCharge);
  const tduVol = use("tdu.volumetricRate", `${tduCfg.name} delivery charge`, "tdu", tduCfg.volumetricRate, o.tduVolumetricRate);
  for (const [v, l] of [[tduCustomer, "tduCustomerCharge"], [tduMeter, "tduMeteringCharge"], [tduVol, "tduVolumetricRate"], [buyback.rate, "buybackRate"], [vppRate, "vppUsdPerKwhMonth"], [octBaseFee, "octopusBaseFee"]] as const) {
    requireFiniteNonNeg(v, l);
  }
  for (const t of TERM_OPTIONS) requireFiniteNonNeg(octRate[t], `octopusEnergyRate.${t}`);
  const tdu: TduPricing = { fixedMonthly: tduCustomer + tduMeter, volumetricRate: tduVol };

  const regionSource = { effectiveDate: regionCfg.effectiveDate, source: regionCfg.source };
  const yieldKwhPerKw = use(
    "region.yieldKwhPerKw",
    `${regionCfg.name} solar yield`,
    "region",
    { value: regionCfg.defaultYieldKwhPerKw, unit: "kWh/kWdc·yr", ...regionSource },
    o.regionYieldKwhPerKw,
  );
  assumptions.push(
    { key: "region.coolingIntensity", label: `${regionCfg.name} cooling intensity`, value: regionCfg.coolingIntensity, unit: "index (ES scale)", ...regionSource, placeholder: false, overridden: false, group: "region" },
    { key: "region.heatingIntensity", label: `${regionCfg.name} heating intensity`, value: regionCfg.heatingIntensity, unit: "index (ES scale)", ...regionSource, placeholder: false, overridden: false, group: "region" },
    { key: "region.latitude", label: `${regionCfg.name} latitude`, value: regionCfg.latitude, unit: "°N", ...regionSource, placeholder: false, overridden: false, group: "region" },
  );

  // ── Home load ───────────────────────────────────────────────────────────
  const archetype: LoadArchetype = input.archetype ?? "electric_heat";
  const homeRes = generateHomeLoad({ climate: regionCfg, archetype, usage: input.usage });
  let components = homeRes.components;

  // ── EV ──────────────────────────────────────────────────────────────────
  const d = config.defaults;
  const evOn = input.ev?.enabled === true;
  let ev: Float64Array | undefined;
  let evRate: number | undefined;
  if (evOn) {
    const evIn = input.ev!;
    const kWhPerMile = use("ev.kWhPerMile", "EV efficiency", "engine", d.ev.kWhPerMile, o.evKwhPerMile);
    const window = use("ev.chargeWindow", "EV smart-charge window", "engine", d.ev.chargeWindow, o.evChargeWindow);
    const annualMiles = use("ev.annualMiles", "EV miles per year", "engine", d.ev.annualMiles, evIn.annualMiles);
    requireFiniteNonNeg(annualMiles, "ev.annualMiles");
    requireFiniteNonNeg(evIn.evRate, "ev.evRate");
    ev = buildEvProfile({ annualMiles, kWhPerMile, window: [window[0]!, window[1]!] });
    if (evIn.mode === "existing") {
      const evMonthly = sumByMonth(ev);
      if (evMonthly.some((k, m) => k > homeRes.monthlyKwh[m]!)) {
        warnings.push("EV energy exceeds recorded usage in at least one month; that month's home load was floored at 0.");
      }
      components = carveOutMonthlyKwh(components, evMonthly);
    }
    evRate = evIn.evRate;
    assumptions.push({
      key: "ev.evRate",
      label: "Octopus EV charging rate",
      value: evRate ?? "same as energy rate",
      unit: "$/kWh",
      effectiveDate: null,
      source: evRate === undefined ? "Default: plan energy rate (no EV discount)" : "Rep-entered",
      placeholder: false,
      overridden: evRate !== undefined,
      group: "octopus",
    });
  }

  // ── Nest ────────────────────────────────────────────────────────────────
  const home0 = composeLoad(components);
  const nestOn = input.nest?.enabled === true;
  let homeC = home0;
  if (nestOn) {
    const pct = use("nest.coolingReductionPct", "Nest cooling reduction", "engine", d.nest.coolingReductionPct, input.nest!.coolingReductionPct);
    if (!(pct >= 0 && pct <= 1)) throw new BidInputError(`nest.coolingReductionPct must be in [0, 1], got ${pct}`);
    homeC = applyNestCoolingReduction(components, pct);
  }

  // ── Solar ───────────────────────────────────────────────────────────────
  const solarOn = input.solar?.enabled === true;
  let solar: Float64Array | undefined;
  if (solarOn) {
    const s = input.solar!;
    requireFiniteNonNeg(s.systemKwDc, "solar.systemKwDc");
    requireFiniteNonNeg(s.annualKwhOverride, "solar.annualKwhOverride");
    if (s.systemKwDc === undefined && s.annualKwhOverride === undefined) {
      throw new BidInputError("solar requires systemKwDc or annualKwhOverride");
    }
    const tilt = use("solar.tiltDeg", "Panel tilt", "engine", d.solar.tiltDeg, s.tiltDeg);
    const az = use("solar.azimuthDeg", "Panel azimuth", "engine", d.solar.azimuthDeg, s.azimuthDeg);
    solar = generateSolarProfile(regionCfg, {
      ...(s.systemKwDc !== undefined ? { systemKwDc: s.systemKwDc } : {}),
      ...(s.annualKwhOverride !== undefined ? { annualKwhOverride: s.annualKwhOverride } : {}),
      yieldKwhPerKwOverride: yieldKwhPerKw,
      tiltDeg: tilt,
      azimuthDeg: az,
    }).hourly;
  }

  // ── Battery ─────────────────────────────────────────────────────────────
  const batteryOn = input.battery?.enabled === true;
  let batt0: BatteryResult | undefined;
  let battC: BatteryResult | undefined;
  let batteryCfg: BatteryConfig | undefined;
  if (batteryOn) {
    const b = input.battery!;
    batteryCfg = {
      capacityKwh: use("battery.capacityKwh", "Battery capacity", "engine", d.battery.capacityKwh, b.capacityKwh),
      powerKw: use("battery.powerKw", "Battery power", "engine", d.battery.powerKw, b.powerKw),
      roundTripEfficiency: use("battery.roundTripEfficiency", "Battery round-trip efficiency", "engine", d.battery.roundTripEfficiency, o.batteryRoundTripEfficiency),
      maxDailyCycles: use("battery.maxDailyCycles", "Battery max cycles/day", "engine", d.battery.maxDailyCycles, o.batteryMaxDailyCycles),
      reservePct,
    };
    requireFiniteNonNeg(batteryCfg.capacityKwh, "battery.capacityKwh");
    requireFiniteNonNeg(batteryCfg.powerKw, "battery.powerKw");
    if (!solarOn) {
      // V1 offering (battery-only): informational for the rep, not an error.
      warnings.push(
        "Battery without solar (V1 offering): held for backup and Octopus VPP dispatch; no arbitrage on a flat rate, so customer bill impact is $0.",
      );
    }
    const solarVec = solar ?? new Float64Array(HOURS_IN_YEAR);
    batt0 = simulateBattery(home0, solarVec, batteryCfg); // home only, never EV
    battC = nestOn ? simulateBattery(homeC, solarVec, batteryCfg) : batt0;
  }

  // ── Flows (incremental build-up) ────────────────────────────────────────
  const evArg = ev ? { ev } : {};
  const fToday = aggregateFlows({ home: home0, ...evArg });
  const fSolar = solar ? aggregateFlows({ home: home0, solar, ...evArg }) : fToday;
  const fBattery = batt0 ? aggregateFlows({ home: home0, netHome: batt0.hourly.netGrid, ...evArg }) : fSolar;
  const fC = !nestOn
    ? fBattery
    : battC
      ? aggregateFlows({ home: homeC, netHome: battC.hourly.netGrid, ...evArg })
      : aggregateFlows({ home: homeC, ...(solar ? { solar } : {}), ...evArg });

  // ── Pricing ─────────────────────────────────────────────────────────────
  const retailA: RetailPricing =
    cp.mode === "itemized"
      ? { energyRate: cp.energyRate, baseFee: cp.baseFee, tduIncluded: false }
      : { energyRate: cp.rate, baseFee: cp.baseFee ?? 0, tduIncluded: true };
  const retailOct = (t: TermMonths, withEvRate: boolean): RetailPricing => ({
    energyRate: octRate[t],
    baseFee: octBaseFee,
    tduIncluded: false,
    ...(withEvRate && evRate !== undefined ? { evRate } : {}),
  });
  const start = input.startMonth;

  const billsA = perTerm((t) => billTerm(fToday, retailA, tdu, NO_BUYBACK, start, t));
  const billsB = perTerm((t) => billTerm(fToday, retailOct(t, false), tdu, buyback, start, t));
  const billsSolar = perTerm((t) => billTerm(fSolar, retailOct(t, false), tdu, buyback, start, t));
  const billsBattery = perTerm((t) => billTerm(fBattery, retailOct(t, false), tdu, buyback, start, t));
  const billsEv = perTerm((t) => billTerm(fBattery, retailOct(t, true), tdu, buyback, start, t));
  const billsC = perTerm((t) => billTerm(fC, retailOct(t, true), tdu, buyback, start, t));

  const diff = (hi: Record<TermMonths, TermBills>, lo: Record<TermMonths, TermBills>) =>
    perTerm<TermSavings>((t) => {
      const total = hi[t].total - lo[t].total;
      return { termMonths: t, total, avgMonthly: total / t };
    });

  const waterfall: WaterfallStep[] = [{ id: "switch", label: "Switch to Octopus", emoji: "🐙", byTerm: diff(billsA, billsB) }];
  if (solarOn) waterfall.push({ id: "solar", label: "Solar", emoji: "☀️", byTerm: diff(billsB, billsSolar) });
  if (batteryOn) waterfall.push({ id: "battery", label: "FREE battery", emoji: "🔋", byTerm: diff(billsSolar, billsBattery) });
  if (evOn) waterfall.push({ id: "ev", label: "EV smart charging", emoji: "🚗", byTerm: diff(billsBattery, billsEv) });
  if (nestOn) waterfall.push({ id: "nest", label: "Nest thermostat", emoji: "🌡️", byTerm: diff(billsEv, billsC) });

  const scenario = (id: ScenarioId, label: string, flows: MonthlyFlows[], byTerm: Record<TermMonths, TermBills>): ScenarioResult => ({
    id,
    label,
    monthlyFlows: flows,
    annualImportsKwh: flows.reduce((a, f) => a + f.importsKwh, 0),
    annualExportsKwh: flows.reduce((a, f) => a + f.exportsKwh, 0),
    byTerm,
  });

  // ── VPP (rep-only; never in a customer bill) ────────────────────────────
  const vppBatteryKwh = batteryOn ? batteryCfg!.capacityKwh : 0;
  const vppMonthly = vppBatteryKwh * vppRate;

  // ── Energy summary ──────────────────────────────────────────────────────
  const homeMonthly = sumByMonth(home0);
  const homeMonthlyNest = sumByMonth(homeC);
  const evMonthly = ev ? sumByMonth(ev) : new Array<number>(12).fill(0);
  const solarMonthly = solar ? sumByMonth(solar) : new Array<number>(12).fill(0);
  const total = (a: number[]) => a.reduce((x, y) => x + y, 0);

  return {
    tdu: input.tdu,
    region: input.region,
    startMonth: start,
    selectedTerm,
    terms: TERM_OPTIONS,
    scenarios: {
      A: scenario("A", "Today", fToday, billsA),
      B: scenario("B", "Switch to Octopus", fToday, billsB),
      C: scenario("C", "Octopus Bid", fC, billsC),
    },
    savings: diff(billsA, billsC),
    waterfall,
    energy: {
      homeAnnualKwh: total(homeMonthly),
      homeMonthlyKwh: homeMonthly,
      homeAnnualKwhWithNest: total(homeMonthlyNest),
      homeMonthlyKwhWithNest: homeMonthlyNest,
      evAnnualKwh: total(evMonthly),
      evMonthlyKwh: evMonthly,
      solarAnnualKwh: total(solarMonthly),
      solarMonthlyKwh: solarMonthly,
      ...(battC && batteryCfg
        ? {
            battery: {
              capacityKwh: batteryCfg.capacityKwh,
              powerKw: batteryCfg.powerKw,
              reserveKwh: battC.summary.minSocKwh,
              usableKwh: battC.summary.maxSocKwh - battC.summary.minSocKwh,
              annualChargedKwh: battC.summary.annualChargedKwh,
              annualDischargedKwh: battC.summary.annualDischargedKwh,
              equivalentCycles: battC.summary.equivalentCycles,
            },
          }
        : {}),
    },
    repOnly: {
      vppProgramValue: {
        batteryKwh: vppBatteryKwh,
        usdPerKwhMonth: vppRate,
        monthly: vppMonthly,
        byTerm: perTerm((t) => vppMonthly * t),
        retainedByOctopus: true,
      },
    },
    assumptions,
    warnings,
  };
}
